/**
 * @fileoverview Due-date offset arithmetic for workflow template items (MOR-836)
 *
 * Pure functions turning a template item's `(amount, unit)` offset into a
 * concrete ISO date at apply time. Kept free of `ctx` so the date math is
 * unit-testable (mirrors `recurrenceDates.ts`).
 *
 * Units:
 * - `days`: calendar days (the original behaviour).
 * - `business_days`: weekdays excluding public holidays in the appointee's
 *   jurisdiction (`convex/lib/businessDays.ts`); negative amounts walk
 *   backwards ("5 business days before the anchor").
 * - `months`: calendar months, clamped to month end (31 Jan + 1 → 28/29 Feb),
 *   unaffected by business-day logic (e.g. 706A report due exactly 3 months
 *   after appointment).
 * - `quarter_end`: calendar days counted from the END of the calendar
 *   quarter containing the anchor (0 = the quarter-end itself, 28 = 28 days
 *   after quarter end, negative = before quarter end).
 *
 * Used by `convex/tasks/workflows.ts` (`applyWorkflowToJobInternal`).
 */

import {
  isBusinessDay,
  PROCLAIMED_BASELINE,
  type AuJurisdiction,
  type ProclaimedHoliday,
} from "@cvx/lib/businessDays";
import type { TaskOffsetUnit } from "@cvx/tasks/taskTemplates/items.schema";

/** Parse ISO YYYY-MM-DD to a UTC Date; null on malformed input. */
function parseIso(iso: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
}

function formatIso(date: Date): string {
  const y = date.getUTCFullYear();
  const mo = String(date.getUTCMonth() + 1).padStart(2, "0");
  const d = String(date.getUTCDate()).padStart(2, "0");
  return `${y}-${mo}-${d}`;
}

/** Add `days` calendar days to an ISO date. */
export function addCalendarDaysIso(iso: string, days: number): string {
  const dt = parseIso(iso);
  if (!dt) return iso;
  dt.setUTCDate(dt.getUTCDate() + days);
  return formatIso(dt);
}

/**
 * Add `months` calendar months, clamping to the last day of the target
 * month when the source day overflows it (31 Jan + 1 month → 28/29 Feb).
 */
export function addCalendarMonthsIso(iso: string, months: number): string {
  const dt = parseIso(iso);
  if (!dt) return iso;
  const day = dt.getUTCDate();
  const target = new Date(
    Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth() + months, 1),
  );
  const lastDay = new Date(
    Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0),
  ).getUTCDate();
  target.setUTCDate(Math.min(day, lastDay));
  return formatIso(target);
}

/**
 * The last day of the calendar quarter containing `iso` (31 Mar / 30 Jun /
 * 30 Sep / 31 Dec).
 */
export function quarterEndIso(iso: string): string {
  const dt = parseIso(iso);
  if (!dt) return iso;
  const quarterEndMonth = Math.floor(dt.getUTCMonth() / 3) * 3 + 2; // 2,5,8,11
  const last = new Date(Date.UTC(dt.getUTCFullYear(), quarterEndMonth + 1, 0));
  return formatIso(last);
}

/**
 * Add `n` business days (signed) to an ISO date. Unlike
 * `businessDays.addBusinessDays` this supports negative amounts, walking
 * backwards over the same weekend/holiday calendar. n = 0 returns the
 * anchor unchanged (even when the anchor itself is not a business day,
 * matching the statutory "N business days after X" framing).
 */
export function addBusinessDaysSigned(
  iso: string,
  n: number,
  jurisdiction?: AuJurisdiction,
  proclaimed: ProclaimedHoliday[] = PROCLAIMED_BASELINE,
): string {
  const dt = parseIso(iso);
  if (!dt || !Number.isInteger(n)) return iso;
  const step = n < 0 ? -1 : 1;
  let remaining = Math.abs(n);
  while (remaining > 0) {
    dt.setUTCDate(dt.getUTCDate() + step);
    if (isBusinessDay(formatIso(dt), jurisdiction, proclaimed)) {
      remaining -= 1;
    }
  }
  return formatIso(dt);
}

export interface OffsetContext {
  jurisdiction?: AuJurisdiction;
  proclaimed?: ProclaimedHoliday[];
}

/**
 * Resolve a template offset to a concrete date.
 *
 * `amount === null/undefined` means "no offset was configured": for the
 * anchor-relative units that yields the anchor itself; for `quarter_end`
 * it still resolves (to the quarter-end date), since choosing the unit
 * alone is meaningful.
 */
export function applyOffset(
  anchorIso: string,
  amount: number | null | undefined,
  unit: TaskOffsetUnit | null | undefined,
  ctx: OffsetContext = {},
): string {
  const resolvedUnit = unit ?? "days";
  const n = amount ?? 0;
  switch (resolvedUnit) {
    case "business_days":
      return addBusinessDaysSigned(
        anchorIso,
        n,
        ctx.jurisdiction,
        ctx.proclaimed ?? PROCLAIMED_BASELINE,
      );
    case "months":
      return addCalendarMonthsIso(anchorIso, n);
    case "quarter_end":
      return addCalendarDaysIso(quarterEndIso(anchorIso), n);
    case "days":
    default:
      return addCalendarDaysIso(anchorIso, n);
  }
}
